/**
 * 打卡任务表单弹窗（家长端共用）。
 *
 * 从 `ParentHabitsPage` 里内联的表单抽出来，原因是「目标」页的叶子节点行
 * 也需要直接加 / 改打卡任务 —— 家长不该为了加一条任务先切到另一个页面。
 * 两个页面共用同一份表单，行为与校验（含 shared 的 `validateRecurrenceRule`）
 * 才不会各写一套、慢慢跑偏。
 *
 * 组件自己管理表单状态与 mutation（`useHabitMutations`）：调用方只需要回答
 * "挂到哪个节点"与"新建还是编辑"，其余（校验、提交、成功提示、错误展示）都在这里。
 *
 * 刻意**不含**页面级内容：奖品分档引导、"打卡任务覆盖情况"这类整页说明留在页面上。
 *
 * ## 积分规则：**每 N 个单位得 M 分**（迁移 0011）
 *
 * 界面上是**两个整数输入框**排成一句话「每 [N] 单位 得 [M] 分」，而不是一个能填
 * `0.5` 的小数框：账本（`points_ledger.delta` / `points_account.balance`）是 `integer`，
 * 小数分值会被末尾的 `floor` 取整成 0（孩子做了事反而拿不到分）。
 * 「每 2 分钟得 1 分」与「0.5 分/分钟」语义完全一样。
 *
 * N、M 的合法性由三层挡住：本文件的 `validatePointsInput()`（最靠前，给人话）、
 * shared 的 `create/updateHabitSchema`（zod）、以及 DB 的
 * `ck_habit_points_unit_size`（`points_unit_size >= 1`，防绕过应用层的写入）。
 * 下限 1 是硬要求：N = 0 会让算分公式除零。
 *
 * 预览那句话（`pointsPreview` → `lib/habitPointsPreview.ts` → shared 的
 * `describePointsRule`）**必须走 shared**：它内部调的是与后端入账同一个
 * `calculateCheckinPoints`，界面承诺的分数因此不可能与真正加的分漂移。
 */
import { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_GRACE_HOURS,
  MAX_POINTS_PER_UNIT,
  MAX_POINTS_UNIT_SIZE,
  MIN_POINTS_UNIT_SIZE,
  RECURRENCES,
  RECURRENCE_LABEL,
  validateRecurrenceRule,
  type Recurrence,
} from '@fht/shared';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { useHabitMutations } from '../../lib/domainQueries';
import { toErrorMessage } from '../../lib/api/error';
import { habitPointsPreview } from '../../lib/habitPointsPreview';
import { cn } from '../../lib/utils';
import type { Habit, HabitWritePayload, NodeSummary } from '../../lib/api/types';

/**
 * 频率标签统一从 shared 取 —— 这里原先自己维护了一份，而 `Recurrence` 新增
 * `once` 之后两份必然走偏（一份漏了新值，界面就会出现空白选项）。
 * 一个概念只留一处文案来源，所以这里直接用共享常量，不再本地定义。
 */


const WEEKDAYS = [
  { value: 1, label: '一' },
  { value: 2, label: '二' },
  { value: 3, label: '三' },
  { value: 4, label: '四' },
  { value: 5, label: '五' },
  { value: 6, label: '六' },
  { value: 0, label: '日' },
] as const;

/** 常用单位快选；家长也可以自己填。 */
const UNIT_PRESETS = ['分钟', '页', '题', '个', '次', '遍', '字'] as const;

export interface HabitFormState {
  name: string;
  recurrence: Recurrence;
  weekdays: number[];
  monthDays: number[];
  lastDayOfMonth: boolean;
  intervalDays: string;
  hasTarget: boolean;
  targetValue: string;
  unit: string;
  /**
   * 「每 `pointsUnitSize` 个单位得 `pointsPerUnit` 分」里的 N（见方案 3.4.1）。
   *
   * 为什么把 N 和 M 拆成两个输入框而不是允许填 `0.5`：账本是整数
   * （`points_ledger.delta` / `points_account.balance`），小数分值会被
   * `floor` 成 0，孩子做了事反而拿不到分。整数对整数能表达同样的粒度：
   * 「每 2 分钟得 1 分」≡「0.5 分/分钟」。
   */
  pointsUnitSize: string;
  pointsPerUnit: string;
  dailyPointsCap: string;
  limitCap: boolean;
  graceHours: string;
  activeFrom: string;
  activeTo: string;
}

function todayString(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** 新建时的初始表单：生效区间默认跟随所挂节点的起止日期。 */
export function emptyHabitForm(node: NodeSummary): HabitFormState {
  return {
    name: '',
    recurrence: 'daily',
    weekdays: [1, 2, 3, 4, 5],
    monthDays: [1],
    lastDayOfMonth: false,
    intervalDays: '2',
    hasTarget: false,
    targetValue: '',
    unit: '分钟',
    pointsUnitSize: '1',
    pointsPerUnit: '1',
    dailyPointsCap: '',
    limitCap: false,
    graceHours: String(DEFAULT_GRACE_HOURS),
    activeFrom: node.startDate || todayString(),
    // 打卡任务的生效区间默认跟随节点的结束日期（长期节点则不限）
    activeTo: node.isLongTerm ? '' : (node.endDate ?? ''),
  };
}

/** 编辑既有打卡任务时把库里的字段还原成表单。 */
export function habitToForm(habit: Habit, node: NodeSummary): HabitFormState {
  const base = emptyHabitForm(node);
  return {
    ...base,
    name: habit.name,
    recurrence: habit.recurrence,
    weekdays: habit.weekdays.length > 0 ? habit.weekdays : base.weekdays,
    monthDays: habit.monthDays.filter((d) => d !== -1).length > 0 ? habit.monthDays.filter((d) => d !== -1) : [1],
    lastDayOfMonth: habit.monthDays.includes(-1),
    intervalDays: String(habit.intervalDays ?? 2),
    hasTarget: habit.targetValue !== null,
    targetValue: habit.targetValue === null ? '' : String(habit.targetValue),
    unit: habit.unit ?? '分钟',
    /**
     * N 的兜底是 **1**：老数据（迁移加列后全是 1）与老后端不返回该字段时，
     * 「每 1 单位得 M 分」正是改动前的语义。兜底成 0 会让算分除零。
     */
    pointsUnitSize: String(habit.pointsUnitSize ?? 1),
    pointsPerUnit: String(habit.pointsPerUnit),
    limitCap: habit.dailyPointsCap !== null,
    dailyPointsCap: habit.dailyPointsCap === null ? '' : String(habit.dailyPointsCap),
    graceHours: String(habit.graceHours),
    activeFrom: habit.activeFrom || base.activeFrom,
    activeTo: habit.activeTo ?? '',
  };
}

/** 表单 → 接口入参。 */
export function formToPayload(form: HabitFormState, node: NodeSummary): HabitWritePayload {
  const monthDays = [...form.monthDays];
  if (form.lastDayOfMonth) monthDays.push(-1);
  return {
    name: form.name.trim(),
    recurrence: form.recurrence,
    weekdays: form.recurrence === 'weekly' ? form.weekdays : [],
    monthDays: form.recurrence === 'monthly' ? monthDays : [],
    intervalDays: form.recurrence === 'custom' ? Number(form.intervalDays) || 1 : null,
    targetValue: form.hasTarget ? Number(form.targetValue) || null : null,
    unit: form.hasTarget ? form.unit.trim() || null : null,
    /**
     * N 的兜底是 **1**（不是 0）：输入框空着 / 非数字 / 小于 1 时，
     * 「每 1 单位得 M 分」= 改动前的语义。提交前另有 `validatePointsInput` 拦住
     * 明显填错的输入并给出人话提示，所以这里只需要一个不崩的兜底。
     */
    pointsUnitSize: Math.max(1, Math.trunc(Number(form.pointsUnitSize)) || 1),
    pointsPerUnit: Number(form.pointsPerUnit) || 0,
    dailyPointsCap: form.limitCap ? Number(form.dailyPointsCap) || 0 : null,
    graceHours: Number(form.graceHours) || 0,
    activeFrom: form.activeFrom,
    activeTo: form.activeTo || (node.isLongTerm ? null : (node.endDate ?? null)),
  };
}

/**
 * 积分规则两个输入框的自检（**整数**校验）。
 *
 * 为什么必须在这里拦：
 *  - `N` 必须是 `>= 1` 的**整数** —— `N = 0` 会让算分公式除零，
 *    小数（如 `2.5`）会让"每 N 个单位"这句话失去意义（单位不可数）；
 *  - `M` 必须是 `>= 0` 的整数，且上限 1000（与 `createHabitSchema` 同口径）。
 *
 * 服务端（zod）与数据库（`ck_habit_points_unit_size`）各有一层，
 * 这里是最靠前的一层，目的是**给出人话**而不是让请求失败。
 */
export function validatePointsInput(
  pointsUnitSize: string,
  pointsPerUnit: string,
): string | null {
  const nRaw = pointsUnitSize.trim();
  const mRaw = pointsPerUnit.trim();

  const n = Number(nRaw);
  if (nRaw === '' || !Number.isInteger(n) || n < MIN_POINTS_UNIT_SIZE || n > MAX_POINTS_UNIT_SIZE) {
    return `「每几个单位」必须是 ${MIN_POINTS_UNIT_SIZE}~${MAX_POINTS_UNIT_SIZE} 的整数（想表达 0.5 分/单位就写「每 2 单位得 1 分」）`;
  }

  const m = Number(mRaw);
  if (mRaw === '' || !Number.isInteger(m) || m < 0 || m > MAX_POINTS_PER_UNIT) {
    return `「得几分」必须是 0~${MAX_POINTS_PER_UNIT} 的整数（填 0 表示只记录完成情况，不给分）`;
  }

  return null;
}

/** 一行"这条打卡任务能得多少分"的说明（纯函数，见 `lib/habitPointsPreview.ts`）。 */
function pointsPreview(form: HabitFormState): string {
  return habitPointsPreview(form);
}

// ---------------------------------------------------------------- 弹窗

export interface HabitFormModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string;
  /** 要挂到的叶子节点（新建时必传；编辑时由调用方给出所属节点）。 */
  node: NodeSummary;
  /** 传 null = 新建；传 habit = 编辑。 */
  editingHabit: Habit | null;
  /** 保存成功后的回调（调用方用来刷新 / 提示）。 */
  onSaved?: () => void;
}

export function HabitFormModal({
  open,
  onClose,
  studentId,
  node,
  editingHabit,
  onSaved,
}: HabitFormModalProps) {
  const toast = useToast();
  const mutations = useHabitMutations(studentId, node.id);

  const [form, setForm] = useState<HabitFormState>(() =>
    editingHabit ? habitToForm(editingHabit, node) : emptyHabitForm(node),
  );
  const [formError, setFormError] = useState<string | null>(null);

  /**
   * 只在"关 → 开"的那一刻重置表单。
   *
   * 为什么不把 `node` / `editingHabit` 放进依赖数组：它们的身份在树每次
   * 重新请求后都会变（React Query 返回新对象），若作为依赖，后台刷新就会把
   * 家长正在填的内容重置掉 —— 与 Modal 里"不要抢输入焦点"是同一类坑。
   */
  const latestRef = useRef({ node, editingHabit });
  useEffect(() => {
    latestRef.current = { node, editingHabit };
  });

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      const { node: targetNode, editingHabit: targetHabit } = latestRef.current;
      setForm(targetHabit ? habitToForm(targetHabit, targetNode) : emptyHabitForm(targetNode));
      setFormError(null);
    }
    wasOpenRef.current = open;
  }, [open]);

  const submit = async () => {
    if (!form.name.trim()) {
      setFormError('请填写打卡任务名称');
      return;
    }
    const payload = formToPayload(form, node);
    // 前端先按 shared 的同一套规则自检一遍，把明显的问题挡在请求之前
    const ruleError = validateRecurrenceRule({
      recurrence: payload.recurrence,
      weekdays: payload.weekdays,
      monthDays: payload.monthDays,
      intervalDays: payload.intervalDays,
      activeFrom: payload.activeFrom,
      activeTo: payload.activeTo,
    });
    if (ruleError) {
      setFormError(ruleError);
      return;
    }
    if (payload.targetValue !== null && !payload.unit) {
      setFormError('设置了目标值就必须填单位（如「分钟」「页」）');
      return;
    }
    // 积分规则：N / M 必须是整数（N ≥ 1）。给的是人话，不是"校验失败"
    const pointsError = validatePointsInput(form.pointsUnitSize, form.pointsPerUnit);
    if (pointsError) {
      setFormError(pointsError);
      return;
    }

    setFormError(null);
    try {
      if (editingHabit) {
        await mutations.update.mutateAsync({ habitId: editingHabit.id, payload });
      } else {
        await mutations.create.mutateAsync({ nodeId: node.id, payload });
      }
      toast.showToast(editingHabit ? '已保存' : '已创建打卡任务', 'success');
      onSaved?.();
      onClose();
    } catch (err) {
      setFormError(toErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editingHabit ? '编辑打卡任务' : '添加打卡任务'}
      description={`挂在「${node.name}」下`}
      size="lg"
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
          <Button loading={mutations.create.isPending || mutations.update.isPending} onClick={() => void submit()}>
            {editingHabit ? '保存' : '创建'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Input
          label="打卡任务名称"
          value={form.name}
          maxLength={64}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="例如：每天阅读 30 分钟"
        />

        {/* 频率 */}
        <Select
          label="频率"
          value={form.recurrence}
          onChange={(e) => setForm({ ...form, recurrence: e.target.value as Recurrence })}
        >
          {RECURRENCES.map((value) => (
            <option key={value} value={value}>
              {RECURRENCE_LABEL[value]}
            </option>
          ))}
        </Select>

        {form.recurrence === 'weekly' && (
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium text-slate-700">选周几（可多选）</legend>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((day) => {
                const active = form.weekdays.includes(day.value);
                return (
                  <button
                    key={day.value}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      setForm({
                        ...form,
                        weekdays: active
                          ? form.weekdays.filter((d) => d !== day.value)
                          : [...form.weekdays, day.value],
                      })
                    }
                    className={cn(
                      'h-10 w-10 rounded-xl text-sm font-semibold transition-colors',
                      active ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                    )}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>
          </fieldset>
        )}

        {form.recurrence === 'monthly' && (
          <fieldset className="space-y-2">
            <legend className="mb-1.5 text-sm font-medium text-slate-700">选每月的哪几天</legend>
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => {
                const active = form.monthDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      setForm({
                        ...form,
                        monthDays: active
                          ? form.monthDays.filter((d) => d !== day)
                          : [...form.monthDays, day],
                      })
                    }
                    className={cn(
                      'h-9 w-9 rounded-lg text-xs font-semibold transition-colors',
                      active ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                    )}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={form.lastDayOfMonth}
                onChange={(e) => setForm({ ...form, lastDayOfMonth: e.target.checked })}
              />
              每月最后一天（自动适配 28/29/30/31 天）
            </label>
          </fieldset>
        )}

        {form.recurrence === 'custom' && (
          <Input
            label="每隔几天"
            type="number"
            min={1}
            max={365}
            value={form.intervalDays}
            onChange={(e) => setForm({ ...form, intervalDays: e.target.value })}
            hint="从生效开始日期起算，例如「每 2 天」表示隔天一次"
          />
        )}

        {/* 量化目标 */}
        <fieldset className="space-y-2 rounded-xl bg-slate-50 p-3">
          <legend className="text-sm font-medium text-slate-700">量化目标（可选）</legend>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={form.hasTarget}
              onChange={(e) => setForm({ ...form, hasTarget: e.target.checked })}
            />
            设置目标值（如 30 分钟、20 页）
          </label>
          {form.hasTarget && (
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="目标值"
                type="number"
                min={1}
                value={form.targetValue}
                onChange={(e) => setForm({ ...form, targetValue: e.target.value })}
              />
              <div className="space-y-1.5">
                <Input
                  label="单位"
                  value={form.unit}
                  maxLength={8}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                />
                <div className="flex flex-wrap gap-1">
                  {UNIT_PRESETS.map((unit) => (
                    <button
                      key={unit}
                      type="button"
                      onClick={() => setForm({ ...form, unit })}
                      className="rounded-lg bg-white px-2 py-0.5 text-xs text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"
                    >
                      {unit}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <p className="text-xs text-slate-500">
            不设目标值时，一次打卡按「1 次」计入统计。
          </p>
        </fieldset>

        {/* 积分规则 */}
        <fieldset className="space-y-2 rounded-xl bg-amber-50/70 p-3">
          <legend className="text-sm font-medium text-amber-900">积分规则</legend>
          {/*
            ⚠️ 对齐约定（**上一次修过的 bug，别弄回来**）。

            结构刻意保持「复选框独占一行 + 下面两列各自只有一行控件」：
              · 复选框自己一行、不在右列里 —— 否则右列的第一个控件会被它**往下推**，
                两个控件的上边缘就不在同一水平线上（用户截图过）；
              · 左列是「一句规则」（每 [N] 单位得 [M] 分），右列是「单日上限」。
                两列的第一个控件都是 `h-11`，所以**上边缘天然对齐**；
                勾选/取消时右列只是多一个/少一个控件，不会把左列推走。

            ⚠️ 窄屏（手机）：
              · `grid-cols-1` 时两列上下堆叠，不会挤成"一个字一行"；
              · 「每 [N] 单位得 [M] 分」三个控件的宽度用 **固定 `w-20` + `shrink-0`**，
                **不用 `flex-1`**（那会退化成 `flex-basis: 0`，窄屏下两个数字框
                被压成一条线）；夹在中间的那些字也是 `shrink-0`，词永远不会被压。
          */}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={form.limitCap}
              onChange={(e) => setForm({ ...form, limitCap: e.target.checked })}
            />
            设置单日积分上限（防刷分）
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {/*
              「每 [N] 单位得 [M] 分」把规则本身写成一句话，家长照着自己的话填数字；
              两个数字框是**整数**（想要 0.5 分/分钟就写「每 2 分钟得 1 分」）。
              这两个数是一件事，所以放进同一个 `role="group"` 里。
            */}
            <div role="group" aria-label="每 N 个单位得 M 分" className="space-y-1.5">
              <span className="block text-sm font-medium text-slate-700">每 N 个单位得 M 分</span>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="shrink-0 text-sm text-slate-600">每</span>
                <input
                  type="number"
                  min={MIN_POINTS_UNIT_SIZE}
                  max={MAX_POINTS_UNIT_SIZE}
                  step={1}
                  inputMode="numeric"
                  aria-label="每几个单位（整数，至少 1）"
                  value={form.pointsUnitSize}
                  onChange={(e) => setForm({ ...form, pointsUnitSize: e.target.value })}
                  className="h-11 w-20 shrink-0 rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
                <span className="shrink-0 text-sm text-slate-600">
                  {form.hasTarget && (form.unit || '').trim() ? (form.unit || '').trim() : '单位'}
                </span>
                <span className="shrink-0 text-sm text-slate-600">得</span>
                <input
                  type="number"
                  min={0}
                  max={MAX_POINTS_PER_UNIT}
                  step={1}
                  inputMode="numeric"
                  aria-label="得几分（整数，0 表示不计分）"
                  value={form.pointsPerUnit}
                  onChange={(e) => setForm({ ...form, pointsPerUnit: e.target.value })}
                  className="h-11 w-20 shrink-0 rounded-xl border border-slate-300 bg-white px-3 text-base text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                />
                <span className="shrink-0 text-sm text-slate-600">分</span>
              </div>
              <p className="text-xs text-slate-500">
                两个数都要填整数；填 0 分表示只记录完成情况、不给分。想要 0.5 分/单位就写「每 2
                单位得 1 分」。
              </p>
            </div>
            {form.limitCap && (
              <Input
                label="单日最多计多少分"
                type="number"
                min={0}
                max={10000}
                value={form.dailyPointsCap}
                onChange={(e) => setForm({ ...form, dailyPointsCap: e.target.value })}
              />
            )}
          </div>
          <p className="text-sm font-medium text-amber-900">💡 {pointsPreview(form)}</p>
        </fieldset>

        {/* 生效区间与补卡 */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            label="生效开始"
            type="date"
            value={form.activeFrom}
            onChange={(e) => setForm({ ...form, activeFrom: e.target.value })}
          />
          <Input
            label="生效结束（可空）"
            type="date"
            value={form.activeTo}
            onChange={(e) => setForm({ ...form, activeTo: e.target.value })}
          />
          <Input
            label="补卡宽限期（小时）"
            type="number"
            min={0}
            max={168}
            value={form.graceHours}
            onChange={(e) => setForm({ ...form, graceHours: e.target.value })}
            hint="默认 24 小时：次日 24:00 前可以把漏打的补上"
          />
        </div>

        {formError && (
          <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {formError}
          </p>
        )}
      </div>
    </Modal>
  );
}
